import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowLeft, Calculator, Copy, FilePlus2, Pencil, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orpc } from '../../../apis/orpc-query';
import { useAuth } from '../../../auth/AuthContext';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { StatCard } from '../../../components/ui/DefinitionList';
import { Dialog } from '../../../components/ui/Dialog';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusText } from '../../../components/ui/StatusText';
import { Tabs } from '../../../components/ui/Tabs';
import { Textarea } from '../../../components/ui/Textarea';
import { formatMoney } from '../../../lib/format';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { TemplateSheet } from '../TemplateSheet';
import { TemplatePreviewTab } from './TemplatePreviewTab';
import { TemplateValuesTab } from './TemplateValuesTab';
import { TemplateVersionsTable } from './TemplateVersionsTable';

type TemplateTab = 'values' | 'versions' | 'preview';

/**
 * `/templates/:id` (plan §7).
 *
 * Three tabs answer the three questions a person has about a template: **what does it say** (Values),
 * **what did it say before** (Versions) and **what does it do to a price** (Preview). Editing the values
 * happens on its own full page, because sixty numbers do not belong in a sheet.
 *
 * Publish and Archive are administrator actions: a salesperson does not see them at all, and the server
 * refuses them anyway (plan §2.3, PP AC02).
 */
export const TemplateDetailPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();
  const { user } = useAuth();

  const isAdmin = user?.role === 'ADMIN';

  const [tab, setTab] = useState<TemplateTab>('values');
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isCloneOpen, setIsCloneOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [publishReason, setPublishReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const templateQuery = useQuery(orpc.templates.get.queryOptions({ input: { templateId: id }, enabled: id !== '' }));
  const catalogQuery = useQuery(orpc.templates.paramCatalog.queryOptions());

  const template = templateQuery.data ?? null;
  const versions = template?.versions ?? [];
  const publishedVersion = versions.find((version) => version.status === 'PUBLISHED') ?? null;
  const draftVersion = versions.find((version) => version.status === 'DRAFT') ?? null;
  const shownVersion = versions.find((version) => version.id === selectedVersionId) ?? publishedVersion ?? draftVersion ?? versions[0] ?? null;

  useEffect(() => {
    setSelectedVersionId(null);
  }, [id]);

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.templates.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.templates.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.templates.previewVersion.key() }),
    ]);
  };

  const onError = (error: unknown): void => setActionError(serverMessage(error) ?? t(submitErrorKey(error)));

  const createDraft = useMutation(
    orpc.templates.createDraftVersion.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        navigate(`/templates/${id}/draft`);
      },
      onError,
    }),
  );

  const cloneTemplate = useMutation(
    orpc.templates.cloneVersion.mutationOptions({
      onSuccess: async (created) => {
        await invalidate();
        navigate(`/templates/${created.id}`);
      },
      onError,
    }),
  );

  const publishVersion = useMutation(
    orpc.templates.publishVersion.mutationOptions({
      onSuccess: async () => {
        setIsPublishOpen(false);
        setPublishReason('');
        await invalidate();
      },
      onError,
    }),
  );

  const archiveTemplate = useMutation(
    orpc.templates.archive.mutationOptions({
      onSuccess: async () => {
        setIsArchiveOpen(false);
        await invalidate();
      },
      onError,
    }),
  );

  if (templateQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (templateQuery.isError || !template) {
    return <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>;
  }

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/templates')}
      >
        <ArrowLeft className="size-3.5" />
        {t('templates.backToList')}
      </Button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{template.name}</h1>
          <p
            dir="ltr"
            className="mt-1 text-start font-mono text-sm text-muted-foreground"
          >
            {template.make} {template.model} · {t(`enums.fuel.${template.fuel}`)}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusText label={t(`templates.statuses.${template.status}`)} />
            {draftVersion ? <Badge tone="info">{t('templates.draftBadge')}</Badge> : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            disabled={publishedVersion === null || template.status === 'ARCHIVED'}
            onClick={() => navigate(`/estimates/new?templateId=${template.id}`)}
          >
            <Calculator className="size-4" />
            {t('templates.startEstimate')}
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsSheetOpen(true)}
          >
            <Pencil className="size-4" />
            {t('templates.editIdentity')}
          </Button>
          <Button
            variant="outline"
            isLoading={createDraft.isPending}
            disabled={template.status === 'ARCHIVED'}
            onClick={() => {
              setActionError(null);

              if (draftVersion) {
                navigate(`/templates/${template.id}/draft`);

                return;
              }

              createDraft.mutate({ templateId: template.id });
            }}
          >
            <FilePlus2 className="size-4" />
            {draftVersion ? t('templates.editDraft') : t('templates.newDraft')}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setCloneName(t('templates.cloneDefaultName', { name: template.name }));
              setIsCloneOpen(true);
            }}
          >
            <Copy className="size-4" />
            {t('templates.clone')}
          </Button>
          {isAdmin ? (
            <>
              <Button
                disabled={draftVersion === null}
                onClick={() => {
                  setPublishReason(draftVersion?.reason ?? '');
                  setIsPublishOpen(true);
                }}
              >
                <Upload className="size-4" />
                {t('templates.publish')}
              </Button>
              <Button
                variant="ghost"
                disabled={template.status === 'ARCHIVED'}
                onClick={() => setIsArchiveOpen(true)}
              >
                <Archive className="size-4" />
                {t('templates.archive')}
              </Button>
            </>
          ) : null}
        </div>
      </header>

      {actionError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('templates.statPublished')}
          value={publishedVersion === null ? t('templates.noPublishedVersion') : `v${publishedVersion.version}`}
          hint={publishedVersion?.publishedByName ?? undefined}
        />
        <StatCard
          label={t('templates.statDraft')}
          value={draftVersion === null ? t('common.notSet') : `v${draftVersion.version}`}
          hint={draftVersion ? t('templates.statDraftHint') : undefined}
        />
        <StatCard
          label={t('templates.statReferencePrice')}
          value={
            shownVersion?.referencePriceDefault === null || shownVersion === null
              ? t('common.notRecorded')
              : formatMoney(shownVersion.referencePriceDefault)
          }
          hint={shownVersion?.referenceSource ?? undefined}
        />
        <StatCard
          label={t('templates.statPreset')}
          value={template.depreciationPreset === null ? t('templates.presetNone') : t(`templates.presets.${template.depreciationPreset}`)}
          hint={t('templates.statPresetHint')}
        />
      </div>

      <Tabs
        items={[
          { value: 'values', label: t('templates.tabValues') },
          { value: 'versions', label: t('templates.tabVersions'), badge: versions.length },
          { value: 'preview', label: t('templates.tabPreview') },
        ]}
        value={tab}
        onChange={setTab}
      />

      {shownVersion === null ? (
        <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">{t('templates.noVersions')}</p>
      ) : tab === 'values' ? (
        <TemplateValuesTab
          version={shownVersion}
          versions={versions}
          catalog={catalogQuery.data}
          onSelectVersion={setSelectedVersionId}
        />
      ) : tab === 'versions' ? (
        <TemplateVersionsTable
          versions={versions}
          hasDraft={draftVersion !== null}
          isOpening={createDraft.isPending}
          onOpenAsDraft={(versionId) => {
            setActionError(null);
            createDraft.mutate({ templateId: template.id, fromVersionId: versionId });
          }}
        />
      ) : (
        <TemplatePreviewTab version={shownVersion} />
      )}

      <TemplateSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        template={template}
      />

      <Dialog
        isOpen={isCloneOpen}
        onClose={() => setIsCloneOpen(false)}
        title={t('templates.cloneTitle')}
        description={t('templates.cloneBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsCloneOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={cloneTemplate.isPending}
              disabled={cloneName.trim() === '' || shownVersion === null}
              onClick={() => {
                setActionError(null);

                if (shownVersion) {
                  cloneTemplate.mutate({ fromVersionId: shownVersion.id, newTemplateName: cloneName.trim() });
                  setIsCloneOpen(false);
                }
              }}
            >
              {t('templates.cloneConfirm')}
            </Button>
          </>
        }
      >
        <FormField
          id="clone-name"
          label={t('templates.name')}
          hint={t('templates.cloneNameHint', { version: shownVersion?.version ?? 1 })}
        >
          <Input
            id="clone-name"
            value={cloneName}
            onChange={(event) => setCloneName(event.target.value)}
          />
        </FormField>
      </Dialog>

      <Dialog
        isOpen={isPublishOpen}
        onClose={() => setIsPublishOpen(false)}
        title={t('templates.publishTitle')}
        description={t('templates.publishBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsPublishOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={publishVersion.isPending}
              disabled={draftVersion === null || publishReason.trim() === ''}
              onClick={() => {
                setActionError(null);

                if (draftVersion) {
                  publishVersion.mutate({
                    versionId: draftVersion.id,
                    expectedRevision: draftVersion.revision,
                    reason: publishReason.trim(),
                  });
                }
              }}
            >
              {t('templates.publishConfirm')}
            </Button>
          </>
        }
      >
        <FormField
          id="publish-template-reason"
          label={t('templates.reason')}
          hint={t('templates.reasonHint')}
        >
          <Textarea
            id="publish-template-reason"
            rows={3}
            value={publishReason}
            onChange={(event) => setPublishReason(event.target.value)}
          />
        </FormField>
      </Dialog>

      <Dialog
        isOpen={isArchiveOpen}
        onClose={() => setIsArchiveOpen(false)}
        title={t('templates.archiveTitle')}
        description={t('templates.archiveBody', { name: template.name })}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsArchiveOpen(false)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              isLoading={archiveTemplate.isPending}
              onClick={() => {
                setActionError(null);
                archiveTemplate.mutate({ templateId: template.id });
              }}
            >
              {t('templates.archiveConfirm')}
            </Button>
          </>
        }
      />
    </div>
  );
};
